import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../../navbar";
import Header from "../../header";
import Pagination from "../dashboard/appPagination";
import {
  clearVerifyResult,
  exportUserAuditLogs,
  fetchUserAuditLogs,
  verifyUserAuditChain,
} from "../../../store/reducers/userAuditSlice";
import { notifyApiError, notifyApiSuccess } from "../../../helpers/apiNotify";
import { canVerifyUserAudit, hasRole, ROLES } from "../../../utility/rbac";
import UserAuditEntryList from "./UserAuditEntryList";
import UserHistoryModal from "./UserHistoryModal";
import {
  USER_AUDIT_ACTION_OPTIONS,
  USER_AUDIT_STATUS_OPTIONS,
  USER_AUDIT_TARGET_ROLE_OPTIONS,
} from "./userAuditConstants";
import "../applications/auditTrail/AuditTrailModal.css";

/**
 * Global user audit log — GET /user-audit-logs
 * Admin: all entries + chain verify. Manager: company admins/employees (API).
 */
const UserAuditTrailPage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const accessToken = user?.access_token;
  const { logs, loading, error, pagination } = useSelector(
    (state) => state.userAudit.global
  );
  const verify = useSelector((state) => state.userAudit.verify);
  const isAdmin = hasRole(user, ROLES.ADMIN) || Boolean(user?.user?.is_admin);
  const showVerify = canVerifyUserAudit(user);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [action, setAction] = useState("");
  const [status, setStatus] = useState("");
  const [targetRole, setTargetRole] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [historyUser, setHistoryUser] = useState(null);

  const filterParams = useCallback(
    () => ({
      action: action || undefined,
      status: status || undefined,
      target_role: targetRole || undefined,
      from: from || undefined,
      to: to || undefined,
    }),
    [action, status, targetRole, from, to]
  );

  useEffect(() => {
    if (!accessToken) return;
    dispatch(
      fetchUserAuditLogs({
        accessToken,
        page,
        limit,
        ...filterParams(),
      })
    );
  }, [accessToken, page, limit, filterParams, dispatch]);

  const resetPageOnFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(1);
  };

  const handleExport = async () => {
    if (!accessToken || downloading) return;
    setDownloading(true);
    try {
      await exportUserAuditLogs({
        accessToken,
        ...filterParams(),
      });
      notifyApiSuccess("Audit log exported");
    } catch (err) {
      notifyApiError(err, "Failed to export audit logs");
    } finally {
      setDownloading(false);
    }
  };

  const handleVerify = async () => {
    if (!accessToken || !showVerify) return;
    try {
      const result = await dispatch(
        verifyUserAuditChain({ accessToken })
      ).unwrap();
      if (result?.valid) {
        notifyApiSuccess(
          `Hash chain intact (${result.checked} entries checked)`
        );
      } else {
        notifyApiError(
          {
            message: `Chain broken at #${result?.brokenAtId || "?"}: ${
              result?.problem || "unknown"
            }`,
          },
          "Audit chain verification failed"
        );
      }
    } catch (err) {
      notifyApiError(
        { message: typeof err === "string" ? err : err?.message },
        "Failed to verify the audit log"
      );
    }
  };

  return (
    <div>
      <Navbar />
      <Header />
      <div className="wrapper">
        <div className="boxAppBoard boxWhite">
          <div className="boardHeader noMg">
            <div className="boardHeadLeft">
              <h2>User Audit Trail</h2>
            </div>
            <div className="boardHeadRight d-flex gap-2 flex-wrap">
              {showVerify && (
                <button
                  type="button"
                  className="audit-page-btn"
                  onClick={handleVerify}
                  disabled={verify.loading}
                >
                  {verify.loading ? "Verifying…" : "Verify hash chain"}
                </button>
              )}
              <button
                type="button"
                className="audit-download-btn"
                onClick={handleExport}
                disabled={downloading}
              >
                {downloading ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          </div>

          {verify.result && (
            <div
              style={{
                margin: "12px 0",
                padding: "10px 12px",
                borderRadius: 4,
                background: verify.result.valid ? "#f0faf0" : "#fff5f5",
                border: `1px solid ${
                  verify.result.valid ? "#b7e1b7" : "#f5c2c2"
                }`,
                fontSize: 13,
              }}
            >
              {verify.result.valid ? (
                <span>
                  Chain valid · checked {verify.result.checked}
                  {verify.result.unchained
                    ? ` · ${verify.result.unchained} unchained (pre-migration)`
                    : ""}
                  {verify.result.lastId
                    ? ` · last id ${verify.result.lastId}`
                    : ""}
                </span>
              ) : (
                <span>
                  Chain invalid at entry #{verify.result.brokenAtId}:{" "}
                  {verify.result.problem}
                  <button
                    type="button"
                    className="btn btn-link btn-sm"
                    onClick={() => dispatch(clearVerifyResult())}
                  >
                    Dismiss
                  </button>
                </span>
              )}
            </div>
          )}
          {verify.error && (
            <div className="audit-modal-error" style={{ marginTop: 8 }}>
              {verify.error}
            </div>
          )}

          <div
            className="audit-modal-filters"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              marginTop: 16,
              marginBottom: 8,
            }}
          >
            <label>
              Action
              <select value={action} onChange={resetPageOnFilter(setAction)}>
                <option value="">All actions</option>
                {USER_AUDIT_ACTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Status
              <select value={status} onChange={resetPageOnFilter(setStatus)}>
                <option value="">All statuses</option>
                {USER_AUDIT_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
            {isAdmin && (
              <label>
                Target role
                <select
                  value={targetRole}
                  onChange={resetPageOnFilter(setTargetRole)}
                >
                  <option value="">All roles</option>
                  {USER_AUDIT_TARGET_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              From
              <input
                type="date"
                value={from}
                onChange={resetPageOnFilter(setFrom)}
              />
            </label>
            <label>
              To
              <input type="date" value={to} onChange={resetPageOnFilter(setTo)} />
            </label>
          </div>

          <div className="audit-modal-body" style={{ maxHeight: "none" }}>
            <UserAuditEntryList
              logs={logs}
              loading={loading}
              error={error}
              onOpenHistory={setHistoryUser}
            />
          </div>

          <Pagination
            currentPage={pagination.currentPage || page}
            totalPages={pagination.totalPages || 1}
            itemsPerPage={limit}
            setCurrentPage={setPage}
            setItemsPerPage={(n) => {
              setLimit(n);
              setPage(1);
            }}
            totalItems={pagination.total || 0}
          />
        </div>
      </div>

      <UserHistoryModal
        open={Boolean(historyUser)}
        onClose={() => setHistoryUser(null)}
        userId={historyUser?.id}
        userLabel={historyUser?.label}
        accessToken={accessToken}
      />
    </div>
  );
};

export default UserAuditTrailPage;
